<template>
  <div class="flex flex-col gap-4">
    <!-- Used by -->
    <div class="bg-slate-700/40 rounded-lg border border-slate-600 p-3">
      <div class="text-xs font-semibold text-slate-300 uppercase tracking-wide mb-2">Used by</div>
      <div v-if="usages.length === 0" class="text-xs text-slate-500">Not used by any widget</div>
      <div v-else class="flex flex-col gap-1">
        <button
          v-for="u in usages"
          :key="u.objectUuid"
          class="text-left text-xs text-cyan-300 hover:text-cyan-200 hover:bg-slate-600/40 rounded px-2 py-1 transition-colors"
          @click="$emit('navigate-to-widget', { objectUuid: u.objectUuid, pageUuid: u.pageUuid })"
        >
          {{ capitalize(u.obj) }} {{ u.haspid }}<span v-if="u.comment" class="text-slate-400"> — {{ u.comment }}</span>
        </button>
      </div>
    </div>

    <!-- Designer-only properties -->
    <div class="bg-slate-700/40 rounded-lg border border-slate-600 p-3 space-y-3">
      <div class="text-xs font-semibold text-slate-300 uppercase tracking-wide">Designer</div>

      <div>
        <form-label class="text-xs text-slate-400 mb-1">Name</form-label>
        <form-input
          class="w-full text-sm"
          :modelValue="font.name"
          placeholder="Font name"
          @input="update('name', $event.target.value)"
        />
      </div>

    </div>

    <!-- Firmware properties (what gets exported) -->
    <div class="bg-slate-700/40 rounded-lg border border-slate-600 p-3 space-y-3">
      <div class="text-xs font-semibold text-slate-300 uppercase tracking-wide">Firmware</div>

      <div>
        <form-label class="text-xs text-slate-400 mb-1">Font ID</form-label>
        <form-input
          class="w-full text-sm font-mono"
          :modelValue="font.id"
          placeholder="e.g. 7seg"
          @input="update('id', $event.target.value)"
        />
        <div v-if="idWarning" class="text-xs text-amber-400 mt-1">{{ idWarning }}</div>
      </div>

      <div>
        <form-label class="text-xs text-slate-400 mb-1">File</form-label>
        <form-input
          class="w-full text-sm font-mono"
          :modelValue="font.file"
          placeholder="L:/7segment.ttf"
          @input="update('file', $event.target.value)"
        />
      </div>
      <!-- No size field here — a Font Resource describes the file, not a
           rendered instance of it. Size is set per-widget (e.g. Label's own
           "Font size" field, shown next to its font picker), since one font
           file is reusable by many widgets at different sizes. -->
    </div>

    <!-- Designer canvas preview — uploads the actual font file so widgets
         referencing this resource can render with it. Never exported;
         `file` above is the separate, firmware-facing on-device path. -->
    <div class="bg-slate-700/40 rounded-lg border border-slate-600 p-3 space-y-2">
      <div class="text-xs font-semibold text-slate-300 uppercase tracking-wide">Canvas preview</div>

      <input
        type="file"
        accept=".ttf,.otf,.woff,.woff2"
        class="text-xs text-slate-300 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-slate-600 file:text-slate-200 file:text-xs hover:file:bg-slate-500"
        @change="onFileSelected"
      />
      <div v-if="uploadError" class="text-xs text-red-400">{{ uploadError }}</div>
      <div v-else-if="!font.storageId" class="text-xs text-slate-500">
        No file uploaded — widgets using this font preview with a fallback family.
      </div>
      <div v-else-if="font.originalFilename" class="text-xs text-slate-500">
        {{ font.originalFilename }}
      </div>

      <div
        v-if="font.storageId"
        class="mt-1 px-2 py-3 bg-slate-800/60 rounded border border-slate-700 text-center overflow-hidden"
        :style="previewStyle"
      >{{ previewText }}</div>
    </div>
  </div>
</template>

<script setup>
/**
 * FontEditor.vue — companion to FontManager.vue, mirroring DatasetEditor.vue's
 * shape (Used-by list, Designer-only fields, Firmware fields). There is no
 * resource-registration line for fonts on the wire — `file` (the on-device
 * path, e.g. "L:/7segment.ttf") is exported directly as a widget's
 * `text_font` when that widget selects this font (see label.js's `font`
 * property and Exporter._processFonts()). `size` lives on widgets, not here.
 *
 * Uploading and loading are two different layers, on purpose: this
 * component calls fontStore.setFontFile() (storage — reads the File,
 * delegates to FontDB, updates metadata) to persist the file, and
 * DesignerFontLoader.loadFont()/getFontFamily() (rendering) purely for its
 * own live preview swatch. FontStore never creates a FontFace itself.
 */
import { ref, computed, watch } from 'vue'
import { useFontStore } from '../../stores/fontStore.js'
import { DesignerFontLoader } from '../../services/DesignerFontLoader.js'
import { findWidgetsReferencing } from '../../utils/resourceUsage.js'

const props = defineProps({
  font: { type: Object, required: true },
  objectlayer: { type: Object, default: null },
})
defineEmits(['navigate-to-widget'])

const store = useFontStore()
const uploadError = ref('')

const PREVIEW_SIZE = 32 // this editor's own preview size — nothing to do with any widget's

// Load (or re-load, after a fresh upload — see onFileSelected's explicit
// unloadFont() below) as soon as this font resource has a file to load.
watch(
  () => props.font.storageId,
  (storageId) => { if (storageId != null) DesignerFontLoader.loadFont(props.font.uuid) },
  { immediate: true },
)

async function onFileSelected(event) {
  const file = event.target.files?.[0]
  if (!file) return
  uploadError.value = ''
  try {
    await store.setFontFile(props.font.uuid, file)
    // Auto-fill the device path when it hasn't been set yet, so the font
    // becomes immediately selectable in the Font Family picker without the
    // user having to type it manually.
    if (!props.font.file) {
      store.updateFont(props.font.uuid, { file: `L:/${file.name}` })
    }
    // A fresh upload gets a new storageId, but the previously-cached
    // FontFace for the OLD bytes is still sitting in DesignerFontLoader —
    // drop it so it reloads from the new file (widgets using this resource
    // pick this up too, via their own reactive watch on its status).
    DesignerFontLoader.unloadFont(props.font.uuid)
  } catch (e) {
    uploadError.value = 'Failed to read that file.'
  }
}

const previewText = computed(() => props.font.name || 'Sample 123')
const previewStyle = computed(() => {
  const family = DesignerFontLoader.getFontFamily(props.font.uuid)
  return {
    fontFamily: family || 'inherit',
    fontSize: `${PREVIEW_SIZE}px`,
    color: '#e2e8f0',
    fontStyle: family ? 'normal' : 'italic', // italicized fallback hints "not yet loaded"
  }
})

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)

// A widget's Font Family selection lives on `font`, holding the literal wire
// string — for a project font, that's this resource's `file` path (see
// label.js's `font` property comment), not its uuid.
const usages = computed(() =>
  props.objectlayer && props.font.file ? findWidgetsReferencing(props.objectlayer, 'font', props.font.file) : []
)

const idWarning = computed(() => {
  if (!props.font.id) return "No font id set — this font can't be exported until it has one."
  const dup = store.all.find((f) => f.uuid !== props.font.uuid && f.id === props.font.id)
  return dup ? `"${props.font.id}" is also used by "${dup.name}" — ids must be unique.` : ''
})

function update(field, value) {
  store.updateFont(props.font.uuid, { [field]: value })
}
</script>
